import React, { useMemo, useCallback, ReactNode } from "react";
import { useNavigate } from "react-router";
import { useTable, createColumnHelper, flexRender, tableFeatures, Row, Cell } from "@tanstack/react-table";

import Dropdown, { DropdownListItem } from "../Dropdown";

const tableFeatureSet = tableFeatures({});
type TableFeatures = typeof tableFeatureSet;

export enum TableCellType {
  TEXT = "TEXT",
  OVERFLOW = "OVERFLOW",
  BUTTON = "BUTTON",
}

export type TableCell = {
  value: React.ReactNode;
  type?: TableCellType;
};

export const SmallCell = (children: ReactNode): TableCell => ({
  value: <div className="flex text-xs whitespace-pre-line max-w-sm">{children}</div>,
});

export type TableRow = {
  id: string | number;
  className?: string;
  type?: string;
  cells: TableCell[];
  to?: string; // Path for navigation when row is clicked
};

export type TableColumn = string | { id: string; header: React.ReactNode };

/**
 * Props for the Table component
 */
export type TableProps = {
  /** Optional header content to display above the table */
  header?: React.ReactNode;
  /** Column headers to display */
  columns: TableColumn[];
  /** Row data to render in the table */
  rows: TableRow[];
  /** Empty state content to display when there are no rows */
  emptyState?: React.ReactNode;
  /** Optional footer content */
  footer?: React.ReactNode;
  /** Callback when a row is clicked */
  onRowClick?: (row: TableRow) => void;
  /** Whether to show pagination controls */
  isPaginated?: boolean;
  /** Whether to show a loading state */
  isLoadingData?: boolean;
  /** Number of items per page */
  pageSize?: number;
  /** Current page number (1-indexed) */
  currentPage?: number;
  /** Current page row count for pagination display */
  pageRowCount?: number;
  /** Callback to go to next page */
  onNextPage?: () => void;
  /** Callback to go to previous page */
  onPrevPage?: () => void;
  /** Callback when page size changes */
  onPageSizeChange?: (size: number) => void;
  /** Available page size options */
  pageSizeOptions?: number[];
};

/**
 * Get the appropriate CSS class for a table cell based on its type
 */
const getCellClassName = (cellType?: TableCellType): string => {
  if (cellType === TableCellType.BUTTON) return "m-auto";
  if (cellType === TableCellType.OVERFLOW) return "px-3 py-2 text-sm text-gray-500";
  return "px-3 py-2 truncate text-sm text-gray-500";
};

const Table = ({
  header,
  columns,
  rows,
  emptyState = "No results.",
  footer,
  onRowClick,
  isPaginated,
  pageSize = 10,
  currentPage = 1,
  onNextPage,
  onPrevPage,
  onPageSizeChange,
  pageSizeOptions,
}: TableProps) => {
  const navigate = useNavigate();

  const handleRowClick = useCallback(
    (event: React.MouseEvent<HTMLTableRowElement>, row: TableRow): void => {
      // Check for text selection - if user selected text, don't navigate
      const selection = window.getSelection();
      if (selection && selection.toString().length > 0) {
        return;
      }

      // Check for interactive elements
      if ((event.target as HTMLElement).closest("button, a, input, [role='button']")) {
        return;
      }

      // Handle navigation if 'to' is present
      if (row.to && row.to.trim() !== "") {
        if (event.metaKey || event.ctrlKey) {
          if (row.to.trim().toLowerCase().startsWith("javascript:")) {
            console.error("Invalid navigation URL detected:", row.to);
            return;
          }
          window.open(row.to, "_blank");
          return;
        }
        navigate(row.to);
        return;
      }

      event.stopPropagation();
      if (onRowClick) {
        onRowClick(row);
      }
    },
    [onRowClick, navigate],
  );

  const columnHelper = createColumnHelper<TableFeatures, TableRow>();

  const columnDefinitions = useMemo(() => {
    return columns.map((column, index) =>
      columnHelper.display({
        id: typeof column === "string" ? column || index.toString() : column.id,
        header: () => <span>{typeof column === "string" ? column : column.header}</span>,
        cell: (info) => {
          const cell = info.row.original.cells[index];
          return (
            <div>
              {cell?.type === TableCellType.BUTTON ? (
                <>{cell.value}</>
              ) : (
                <div className="inline-block cursor-text">{cell?.value}</div>
              )}
            </div>
          );
        },
      }),
    );
  }, [columns, columnHelper]);

  const table = useTable({
    features: tableFeatureSet,
    data: rows,
    columns: columnDefinitions,
  });

  /**
   * Render a standard cell
   */
  const renderStandardCell = useCallback(
    (row: Row<TableFeatures, TableRow>, cell: Cell<TableFeatures, TableRow, unknown>, idx: number) => {
      const cellType = row.original.cells[idx].type;

      return (
        <td key={`${row.id}-${idx}`} className={getCellClassName(cellType)}>
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </td>
      );
    },
    [],
  );

  /**
   * Render a table row with navigation
   */
  const renderNavigableRow = useCallback(
    (row: Row<TableFeatures, TableRow>) => {
      const rowClassNames = `${row.original.className || ""} hover:bg-gray-50/50 cursor-pointer transition-colors`;
      const cells = row.getAllCells().map((cell, idx) => renderStandardCell(row, cell, idx));

      return (
        <tr
          key={row.id}
          className={rowClassNames}
          tabIndex={0}
          aria-label={`Navigate to ${row.original.to}`}
          data-testid={`row-link-${row.id}`}
          onClick={(event) => handleRowClick(event, row.original)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && row.original.to) {
              navigate(row.original.to);
            }
          }}
        >
          {cells}
        </tr>
      );
    },
    [renderStandardCell, handleRowClick, navigate],
  );

  /**
   * Render a standard table row
   */
  const renderStandardRow = useCallback(
    (row: Row<TableFeatures, TableRow>) => {
      const rowClassNames = `${row.original.className || ""} ${onRowClick ? "hover:bg-gray-50/50 cursor-pointer transition-colors" : ""}`;

      const cells = row.getAllCells().map((cell, idx) => renderStandardCell(row, cell, idx));

      return (
        <tr
          key={row.id}
          className={rowClassNames}
          onClick={onRowClick ? (event) => handleRowClick(event, row.original) : undefined}
        >
          {cells}
        </tr>
      );
    },
    [onRowClick, handleRowClick, renderStandardCell],
  );

  /**
   * Main row rendering function
   */
  const renderRow = useCallback(
    (row: Row<TableFeatures, TableRow>) => {
      // if a row has a non-empty 'to' property, make it navigable
      if (row.original.to && row.original.to.trim() !== "") {
        return renderNavigableRow(row);
      }

      // Otherwise, return a regular row
      return renderStandardRow(row);
    },
    [renderNavigableRow, renderStandardRow],
  );

  /**
   * Render pagination controls
   */
  const renderPagination = useMemo(() => {
    if (!isPaginated || rows.length === 0) {
      return null;
    }

    const rowCount = rows.length;
    const startResult = (currentPage - 1) * pageSize + 1;
    const endResult = startResult + rowCount - 1;

    return (
      <div className="flex justify-between items-center px-2 py-1.5">
        <p className="text-sm text-gray-500">
          Results {startResult} {rowCount > 1 ? `to ${endResult}` : `of ${startResult}`}
        </p>
        <div className="flex items-center gap-2">
          {onPageSizeChange && pageSizeOptions && (
            <Dropdown
              label="Rows per page:"
              inline
              items={pageSizeOptions.map((size) => ({ key: `${size}`, text: `${size}` }))}
              selectedItem={{ key: `${pageSize}`, text: `${pageSize}` }}
              setSelectedItem={(item: DropdownListItem<string>) => onPageSizeChange(+item.key)}
              className="shrink-0"
            />
          )}
          <button
            onClick={onPrevPage}
            disabled={!onPrevPage}
            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-md hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Previous page"
          >
            Previous
          </button>
          <button
            onClick={onNextPage}
            disabled={!onNextPage}
            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-md hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Next page"
          >
            Next
          </button>
        </div>
      </div>
    );
  }, [isPaginated, onNextPage, onPrevPage, pageSize, currentPage, rows.length]);

  const hasRows = table.getRowModel().rows.length > 0;

  const tableHead = (
    <thead className="bg-gray-100">
      {table.getHeaderGroups().map((headerGroup) => (
        <tr key={headerGroup.id} className="border-b border-gray-200">
          {headerGroup.headers.map((header) => (
            <th scope="col" key={header.id} className="px-3 py-2 text-left text-xs font-semibold text-gray-900">
              <div className="flex items-center space-x-1">
                {flexRender(header.column.columnDef.header, header.getContext())}
              </div>
            </th>
          ))}
        </tr>
      ))}
    </thead>
  );

  return (
    <div className="flex flex-col gap-1">
      {header && <div>{header}</div>}
      <div className="overflow-hidden rounded-lg border border-gray-200">
        <div className="overflow-auto">
          <table className="w-full table-auto" role="grid">
            {tableHead}
            {hasRows && (
              <tbody className="divide-y divide-gray-100">
                {table.getRowModel().rows.map(renderRow)}
                {footer && (
                  <tr>
                    <td
                      colSpan={columns.length}
                      className="px-3 py-2 whitespace-nowrap text-sm font-normal text-gray-400"
                    >
                      {footer}
                    </td>
                  </tr>
                )}
              </tbody>
            )}
          </table>
        </div>
        {!hasRows && emptyState !== null && (
          <div className="px-3 py-8 text-center text-sm text-gray-500">{emptyState}</div>
        )}
        {!hasRows && footer && (
          <div className="border-t border-gray-100 px-3 py-2 text-sm font-normal text-gray-400">{footer}</div>
        )}
      </div>
      {renderPagination}
    </div>
  );
};

export default Table;
